/**
 * SupplierProfilePage — /suppliers/:id
 *
 * Conversion-optimised public supplier page. Structure:
 *   Hero (image + name + location + verified pill)
 *   Signal chips row (⚡ reply speed · 📄 certs ready · 🔥 booked N this week)
 *   "From £N" anchor + momentum copy
 *   Trust-stack checklist (verified · response-rate · instant · date match)
 *   Certificate Preview (PAT + PLI cards)
 *   Quick info / gallery / description
 *   Mid-scroll CTA + sticky bottom CTA
 *
 * Core goal: user taps "Message" in <10 seconds with full trust context.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, ChatCircle, Lightning, Trophy, Fire, CheckCircle,
  FileText, ShieldCheck, Clock, Calendar,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import EnquiryFormModal from '../components/EnquiryFormModal';
import FayreInlinePromo from '../components/FayreInlinePromo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_CONFIG = {
  fast_responder: { label: 'Fast Responder', Icon: Lightning, cls: 'bg-[#B89762]/40/15 text-[#C29D52]/85 border-[#B89762]/45/30' },
  highly_booked:  { label: 'Highly Booked',  Icon: Trophy,    cls: 'bg-emerald-400/15 text-emerald-200 border-emerald-400/30' },
  popular:        { label: 'Popular',        Icon: Fire,      cls: 'bg-amber-400/15 text-amber-200 border-amber-400/30' },
};

const CATEGORY_LABEL = {
  dj: 'DJ / Band', photographer: 'Photographer', videographer: 'Videographer',
  photobooth: 'Photo Booth', caterer: 'Caterer',
  florist: 'Florist', venue: 'Venue', cake_maker: 'Cake Maker',
  transport: 'Transport', decorator: 'Decorator',
  wedding_planner: 'Wedding Planner', hair_makeup: 'Hair & Makeup',
};

const CATEGORY_EMOJI = {
  dj: '🎧', photographer: '📷', videographer: '🎥',
  photobooth: '📸', caterer: '🍽️',
  florist: '🌼', venue: '🏛️', cake_maker: '🎂',
  transport: '🚗', decorator: '🎨',
  wedding_planner: '✨', hair_makeup: '💅',
};

// Reply-speed bucket → short, honest copy + full label.
// Deliberately no fake-precise minutes.
const REPLY_BUCKET = {
  within_15m: { short: 'Replies within 15 mins', cta: '15 mins' },
  within_1h:  { short: 'Replies within 1 hour',  cta: '1 hour' },
  same_day:   { short: 'Replies same day',       cta: 'today' },
};

const formatPrice = (n) => {
  if (typeof n !== 'number' || n <= 0) return null;
  return `£${Math.round(n).toLocaleString('en-GB')}`;
};

const formatExpiryShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const SupplierProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [starting, setStarting] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [upcomingFayre, setUpcomingFayre] = useState(null);

  useEffect(() => {
    axios.get(`${API}/suppliers/${id}`)
      .then(r => setSupplier(r.data.supplier))
      .catch(() => setNotFound(true));
    // "See them live" — most-imminent fayre this supplier is attending.
    // Best-effort, never blocks. Returns the soonest upcoming fayre.
    axios.get(`${API}/suppliers/${id}/fayres`, { params: { limit: 1 } })
      .then(r => setUpcomingFayre((r.data.fayres || [])[0] || null))
      .catch(() => setUpcomingFayre(null));
  }, [id]);

  const startChat = async () => {
    if (!user) {
      toast('Sign in to message this supplier');
      navigate('/login');
      return;
    }
    // Before-you-enquire form — gates the first message so the supplier
    // receives a qualified lead, not a "hey r u available" one-liner.
    setEnquiryOpen(true);
  };

  if (notFound) return (
    <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center p-8">
      <div className="text-center">
        <p className="text-white font-bold text-lg mb-2">Supplier not found</p>
        <button onClick={() => navigate('/suppliers')} className="text-[#C29D52] text-sm underline">Back to directory</button>
      </div>
    </div>
  );

  if (!supplier) return (
    <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center" data-testid="supplier-loading">
      <p className="text-white/40 text-sm">Loading…</p>
    </div>
  );

  const badges = supplier.badges || [];
  const ps = supplier.profile_signals || {};
  const replyCopy = ps.reply_speed_bucket ? REPLY_BUCKET[ps.reply_speed_bucket] : null;
  const priceLabel = formatPrice(ps.min_price);
  const certs = supplier.certificates_preview || [];
  const firstName = supplier.business_name.split(' ')[0];

  // CTA subtitle — honest + specific when we have data
  const ctaSub = replyCopy
    ? `⚡ Most customers get a reply ${
        ps.reply_speed_bucket === 'within_15m' ? 'in under 15 mins'
        : ps.reply_speed_bucket === 'within_1h' ? 'within the hour'
        : 'the same day'
      }`
    : '⚡ Message to check availability & pricing';

  return (
    <div className="min-h-screen bg-[#0F1014] text-white pb-28" data-testid="supplier-profile">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-[#0F1014]/95 backdrop-blur border-b border-white/5 px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white" data-testid="back-btn">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <p className="font-bold text-sm truncate flex-1">{supplier.business_name}</p>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-6">
        {/* Hero image */}
        {supplier.images?.[0] ? (
          <div
            className="w-full h-56 md:h-72 rounded-2xl mb-4 bg-cover bg-center"
            style={{ background: `url(${supplier.images[0]}) center/cover` }}
          />
        ) : (
          <div
            className="w-full h-40 rounded-2xl mb-4 flex items-center justify-center text-5xl"
            style={{ background: 'linear-gradient(135deg, rgba(108,85,224,0.3), rgba(194,157,82,0.2))' }}
          >
            {CATEGORY_EMOJI[supplier.category] || '🎯'}
          </div>
        )}

        <h1 className="text-3xl font-black leading-tight">{supplier.business_name}</h1>
        <p className="text-white/50 text-sm flex items-center gap-1 mt-1">
          <MapPin size={13} weight="bold" /> {supplier.city || supplier.postcode}
          {supplier.radius_miles && <span className="text-white/30"> · covers {supplier.radius_miles}mi</span>}
        </p>

        {/* ── Hero signal chips — the conversion row ────────────── */}
        <div className="flex flex-wrap gap-2 mt-3" data-testid="hero-signals">
          {supplier.is_verified && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border border-emerald-400/40 bg-emerald-400/15 text-emerald-200"
              data-testid="signal-verified"
              title="PAT + PLI on file and in-date"
            >
              <CheckCircle size={12} weight="fill" /> Fully Verified
            </span>
          )}
          {replyCopy && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border border-[#B89762]/45/30 bg-[#B89762]/40/10 text-[#C29D52]/85"
              data-testid={`signal-reply-${ps.reply_speed_bucket}`}
            >
              <Lightning size={12} weight="fill" /> {replyCopy.short}
            </span>
          )}
          {ps.has_certificates && certs.length > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border border-[#B89762]/40/30 bg-[#B89762]/30/5 text-[#C29D52]/85"
              data-testid="signal-certs-ready"
            >
              <FileText size={12} weight="fill" /> Certificates ready to send
            </span>
          )}
          {ps.bookings_this_week > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border border-amber-400/30 bg-amber-400/10 text-amber-200"
              data-testid="signal-bookings-week"
            >
              <Fire size={12} weight="fill" /> Booked {ps.bookings_this_week} {ps.bookings_this_week === 1 ? 'time' : 'times'} this week
            </span>
          )}
        </div>

        {/* Earned badges row (Fast Responder / Highly Booked / Popular) + availability */}
        {(badges.length > 0 || supplier.availability_confidence?.label) && (
          <div className="flex flex-wrap gap-2 mt-2" data-testid="profile-badges">
            {badges.map((b) => {
              const c = BADGE_CONFIG[b];
              if (!c) return null;
              const Ic = c.Icon;
              return (
                <span
                  key={b}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border ${c.cls}`}
                  data-testid={`badge-${b}`}
                >
                  <Ic size={12} weight="fill" /> {c.label}
                </span>
              );
            })}
            <ProfileConfidenceBadge conf={supplier.availability_confidence} />
          </div>
        )}

        {/* "From £N" price anchor */}
        <div className="mt-4 flex items-baseline gap-2" data-testid="price-anchor">
          {priceLabel ? (
            <>
              <span className="text-white/50 text-xs font-bold uppercase tracking-wider">From</span>
              <span className="text-2xl font-black text-white">{priceLabel}</span>
              <span className="text-white/40 text-[11px]">· typical booking</span>
            </>
          ) : (
            <span className="text-white/50 text-xs">Request a quote — pricing depends on your event</span>
          )}
        </div>

        {/* ── Trust Stack Card ─────────────────────────────────── */}
        <TrustStack supplier={supplier} />

        {/* ── Certificate Preview ──────────────────────────────── */}
        {ps.has_certificates && certs.length > 0 && (
          <div className="mt-5 rounded-xl p-4 bg-white/[0.03] border border-emerald-400/15" data-testid="certificates-preview">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={16} weight="fill" className="text-emerald-300" />
              <p className="text-[10px] font-black uppercase tracking-[2px] text-emerald-200">Verified Documents</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {certs.map((c) => (
                <div
                  key={c.type}
                  className="rounded-lg px-3 py-2.5 bg-white/[0.04] border border-white/10 flex items-center gap-2"
                  data-testid={`cert-preview-${c.type}`}
                >
                  <CheckCircle size={14} weight="fill" className="text-emerald-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold leading-tight">
                      {c.type === 'pat' ? 'PAT Certificate' : 'Public Liability Insurance'}
                    </p>
                    {c.expiry_date && (
                      <p className="text-[10px] text-white/40 mt-0.5">
                        Valid until {formatExpiryShort(c.expiry_date)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-white/40 mt-2.5 italic">
              Originals can be sent by {firstName} directly in chat.
            </p>
          </div>
        )}

        {/* Quick info */}
        <div className="mt-5 rounded-xl p-4 bg-white/[0.02] border border-white/5" data-testid="quick-info">
          <p className="text-[10px] font-black uppercase tracking-[2px] text-white/40 mb-2">At a glance</p>
          <ul className="space-y-1.5 text-sm text-white/80">
            <li className="flex items-center gap-2"><MapPin size={12} weight="bold" className="text-white/40" />{supplier.city || supplier.postcode}</li>
            <li className="flex items-center gap-2"><Calendar size={12} weight="bold" className="text-white/40" />Available for bookings</li>
            <li className="flex items-center gap-2"><span className="w-3 text-center text-xs">🎯</span>{CATEGORY_LABEL[supplier.category] || 'Event services'}</li>
          </ul>
        </div>

        {/* See them live — bridges online discovery to in-person fayre
           visits. Renders only when this supplier has an upcoming
           approved fayre. */}
        {upcomingFayre && (
          <div className="mt-4">
            <FayreInlinePromo
              fayre={upcomingFayre}
              variant="compact"
              testId="supplier-profile-fayre"
            />
          </div>
        )}

        {/* Gallery */}
        {supplier.images?.length > 1 && (
          <div className="mt-6">
            <p className="text-[10px] font-black uppercase tracking-[2px] text-white/40 mb-2">Gallery</p>
            <div className="grid grid-cols-3 gap-2">
              {supplier.images.slice(1).map((src, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-lg bg-cover bg-center"
                  style={{ background: `url(${src}) center/cover` }}
                  data-testid={`gallery-img-${i}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Mid-scroll CTA */}
        <div
          className="mt-6 mb-6 rounded-2xl p-5 text-center"
          style={{ background: 'linear-gradient(135deg, rgba(108,85,224,0.15), rgba(194,157,82,0.08))', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="font-bold text-sm mb-1">Ready to book or check availability?</p>
          <p className="text-white/50 text-xs mb-3" data-testid="mid-cta-subtitle">{ctaSub}</p>
          <button
            onClick={startChat}
            disabled={starting}
            className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="mid-cta-btn"
          >
            <ChatCircle size={16} weight="fill" />
            {starting ? 'Opening chat…' : `Message ${firstName}`}
          </button>
        </div>

        {/* Description — below CTAs so it's reinforcement, not friction */}
        {supplier.description && (
          <div>
            <p className="text-[10px] font-black uppercase tracking-[2px] text-white/40 mb-2">About</p>
            <div className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap" data-testid="supplier-description">
              {supplier.description}
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom CTA — always visible */}
      <div className="fixed bottom-0 left-0 right-0 z-30 p-3 border-t border-white/10 bg-[#0F1014]/95 backdrop-blur">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={startChat}
            disabled={starting}
            className="w-full py-3.5 rounded-xl text-base font-bold text-white flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="message-supplier-btn"
          >
            <ChatCircle size={18} weight="fill" />
            {starting ? 'Opening chat…' : `Message ${supplier.business_name}`}
          </button>
          <p className="text-white/40 text-[10px] text-center mt-1.5" data-testid="sticky-cta-subtitle">
            {ctaSub}
          </p>
        </div>
      </div>
      {enquiryOpen && (
        <EnquiryFormModal
          supplier={supplier}
          onClose={() => setEnquiryOpen(false)}
          onSubmitted={(chatId) => { setEnquiryOpen(false); navigate(`/chat/${chatId}`); }}
        />
      )}
    </div>
  );
};

export default SupplierProfilePage;

// ── Trust Stack — the "remove hesitation before chat" card ──────────
// Shows only the rows that are actually true. We never show red X's —
// absent facts simply don't render (positive framing, no false signals).
const TrustStack = ({ supplier }) => {
  const ps = supplier.profile_signals || {};
  const rows = [];
  if (supplier.is_verified) {
    rows.push({ key: 'verified', label: 'PAT & Insurance verified', icon: ShieldCheck, tone: 'emerald' });
  }
  if (typeof ps.response_rate_pct === 'number' && ps.response_rate_pct >= 60) {
    rows.push({ key: 'response-rate', label: `${ps.response_rate_pct}% response rate`, icon: Clock, tone: 'cyan' });
  }
  if (ps.instant_bookable) {
    rows.push({ key: 'instant', label: 'Instant booking available', icon: Lightning, tone: 'cyan' });
  }
  if (supplier.availability_confidence?.label === 'highly_reliable') {
    rows.push({ key: 'confidence', label: 'Calendar kept up to date', icon: Calendar, tone: 'emerald' });
  }
  if (!rows.length) return null;

  const toneCls = {
    emerald: 'text-emerald-300',
    cyan: 'text-[#C29D52]',
  };

  return (
    <div className="mt-5 rounded-xl p-4 bg-white/[0.03] border border-white/10 space-y-2" data-testid="trust-stack">
      {rows.map((r) => {
        const Ic = r.icon;
        return (
          <p key={r.key} className="text-sm flex items-center gap-2" data-testid={`trust-row-${r.key}`}>
            <CheckCircle size={14} weight="fill" className={toneCls[r.tone]} />
            <Ic size={13} weight="fill" className="text-white/40" />
            <span className="font-bold">{r.label}</span>
          </p>
        );
      })}
    </div>
  );
};

// Availability-confidence badge — same three-label system as the directory,
// styled slightly larger for the hero. Hidden when confidence is absent or
// zero (never show negative framing on a supplier's own profile).
const PROFILE_CONFIDENCE = {
  highly_reliable: { dot: '🟢', label: 'Highly reliable',      cls: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10' },
  getting_there:   { dot: '🔵', label: 'Getting there',        cls: 'text-sky-300 border-sky-400/40 bg-sky-500/10' },
  limited:         { dot: '🟠', label: 'Limited availability', cls: 'text-amber-300 border-amber-400/40 bg-amber-500/10' },
};

const ProfileConfidenceBadge = ({ conf }) => {
  if (!conf || !conf.label || !PROFILE_CONFIDENCE[conf.label]) return null;
  const cfg = PROFILE_CONFIDENCE[conf.label];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border ${cfg.cls}`}
      data-testid={`profile-confidence-${conf.label}`}
      aria-label={cfg.label}
    >
      <span className="text-[10px]">{cfg.dot}</span> {cfg.label}
    </span>
  );
};
